import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import * as yup from "yup";
import { MCard } from "components/Shared";
import { getInspectorCaseMiles, setInspectorCaseMiles } from "requests/inspectorTimeCard";
import { formatDecimalPercent } from "components/Shared/Formatters";
import { DATA_TYPE_NUMBER_RAW, DATA_TYPE_DATE_WITHDOW, DATA_TYPE_TEXT_BUTTON, DATA_TYPE_BOOLEAN } from "typeConstants/dataTypes";
import MGrid, { COL_CODE_CONTROL_NUMBER, ACTION_SAVE, ACTION_DOWNLOAD, FILTER_TYPE_NONE, FORMATTER_RIGHT_ALIGN } from "components/Shared/MGrid";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { InspectorCaseMileageDto, UpdateInspectorCaseMileageDto } from "interfaces/Mueller.Business.Models";

interface InspectorCaseMileageDtoStupid extends InspectorCaseMileageDto {
  wasEdited?: React.ReactNode;
  button?: React.ReactNode;
}

interface Props {
  inspectorNumber: number;
  payPeriodId: number;
  payPeriodClosed?: boolean;
  makeToast?: (message: string, type: "success" | "error") => void;
  onControlNumberSelected?: (controlNumber: number) => void;
}

const CaseMileage = ({ inspectorNumber, payPeriodId, payPeriodClosed, makeToast, onControlNumberSelected }: Props) => {
  const mounted = useRef<boolean>(true);
  const [miles, setMiles] = useState<InspectorCaseMileageDtoStupid[]>(null);
  const [editDialogBoxVisible, setEditDialogBoxVisible] = useState<boolean>(false);
  const [currentlyEditingId, setCurrentlyEditingId] = useState<number>(null);
  const [mileUpdates, setMileUpdates] = useState<UpdateInspectorCaseMileageDto[]>([]);

  useEffect(() => {
    setMiles(null);
    getInspectorCaseMiles(inspectorNumber, payPeriodId, transformData, mounted);
  }, [inspectorNumber, payPeriodId]);

  const cellToolTip = data => data.mileagePointDetails.map(d => `${d.order}-${d.wayPointTypeName}: ${d.fullAddress}`).join("\n");

  const transformData = data => {
    data.forEach(s => {
      s.wasEdited = ([]);
      s.button = (
        <div className="edit-col">
          <IconButton
            onClick={() => handleEditRow(s.controlIdNumber)}
            disabled={payPeriodClosed}
            title="Edit"
            size="small"
          >
            <Icon>create</Icon>
          </IconButton>
        </div>
      );
    });
    setMiles(data);
  };

  const handleEditRow = (id) => {
    setCurrentlyEditingId(id);
    setEditDialogBoxVisible(true);
  };

  const editDialogBox = () => {
    if (!miles || (miles && !miles.length)) {
      return <div />;
    } else if (!currentlyEditingId) {
      return <div />;
    }

    let fieldToEdit = miles.find(s => s.controlIdNumber === currentlyEditingId);
    return (
      <div className="clockinout-dialog-box">
        <Dialog
          open={editDialogBoxVisible}
          onClose={() => { }}
        >
          <DialogContent sx={{ padding: 0 }}>
            <MCard
              title="Edit Miles"
              fields={[
                {
                  key: "mileageActualEdit",
                  label: "Actual Mileage",
                  value: fieldToEdit.mileageActual,
                  dataType: DATA_TYPE_NUMBER_RAW,
                  validation: yup.number("Numeric Value Required").required("Actual Mileage is a required field")
                },
              ]}
              startInEditMode={true}
              isEditable={true}
              onEditCancelled={() => {
                setCurrentlyEditingId(null);
                setEditDialogBoxVisible(false);
              }}
              onEditFinished={(arr, obj) => handleEditConfirm(obj)}
            />
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  const handleEditConfirm = (obj) => {
    let newMiles = [...miles];
    let newMileUpdate = [...mileUpdates];

    let indexToUpdate = newMiles.findIndex(s => s.controlIdNumber === currentlyEditingId);
    if (indexToUpdate !== -1) {
      if (obj.mileageActualEdit) newMiles[indexToUpdate].mileageActual = obj.mileageActualEdit;
      newMiles[indexToUpdate].wasEdited = (
        <div style={{ textAlign: "center" }}>
          <Icon>update</Icon>
        </div>
      );
    }
    let updatedMileEntry = newMiles.find(s => s.controlIdNumber === currentlyEditingId);
    newMileUpdate.push({
      controlIdNumber: currentlyEditingId,
      mileageActual: updatedMileEntry.mileageActual,
    });
    setCurrentlyEditingId(null);
    setEditDialogBoxVisible(false);
    setMileUpdates(newMileUpdate);

    // Force Reload
    const reload = new Promise((resolve) => {
      setMiles(null);
      resolve(null);
    });
    reload.then(() => setMiles(newMiles));
  };

  const actions = [{
    code: mileUpdates.length && ACTION_SAVE,
    onClick: () => {
      setInspectorCaseMiles(mileUpdates.map(s =>
        ({ controlNumber: s.controlIdNumber, milesActual: parseFloat(s.mileageActual) })),
        () => {
          setMileUpdates([]);
          setMiles(null);
          getInspectorCaseMiles(inspectorNumber, payPeriodId, transformData, mounted);
          makeToast("Save Successful", "success");
        }, mounted);
    },
    className: "md-btn--hover md-pointer--hover",
  }, {
    code: ACTION_DOWNLOAD,
  }];
  
  const onCellClick = (row, column) => {
    const rowItem = miles[row];
    switch (column) {
      case "controlIdNumber":
        if (onControlNumberSelected) {
          onControlNumberSelected(rowItem.controlIdNumber);
        }
        break;
    }
  };

  return (
    <>
      <MCard className="field-rep-case-mileage" title="Case Mileage">
        {miles && miles.length === 0 ?
          "No mileage data found" :
          <MGrid
            headerText="FR Case Mileage"
            className="qa-table-employee-timelogs"
            onCellButtonClick={onCellClick}
            columnDefinitions={[
              { 
                key: "controlIdNumber",
                code: COL_CODE_CONTROL_NUMBER,
                dataType: DATA_TYPE_TEXT_BUTTON,
                dataSource: "controlIdNumber",
                tooltip: () => "Click to view case",
              },
              {
                key: "wasEditedIcon",
                name: (
                  <div title="Edited" style={{ textAlign: "center" }}>
                    <Icon>update</Icon>
                  </div>
                ),
                width: 40,
                dataSource: "wasEdited",
                filterType: FILTER_TYPE_NONE,
                tooltip: row => (row.wasEdited ? "Edited" : "")
              },
              { key: "inspectedDate", name: "Inspected Date", dataSource: "inspectedDate", dataType: DATA_TYPE_DATE_WITHDOW, width: 130 },
              { key: "previousCase", name: "Previous Case/Address", dataSource: "previousCaseOrAddress" },
              { key: "actual", name: "Actual Mileage", tooltip: cellToolTip, dataSource: "mileageActual", dataType: DATA_TYPE_NUMBER_RAW, width: 130, editable: !payPeriodClosed },
              { key: "estimated", name: "Estimated Mileage", dataSource: "mileageEstimated", dataType: DATA_TYPE_NUMBER_RAW, width: 144, formatter: FORMATTER_RIGHT_ALIGN },
              { key: "efficiency", name: "Efficiency %", dataSource: row => formatDecimalPercent(row.efficiencyPercent), dataType: DATA_TYPE_NUMBER_RAW, width: 110, formatter: FORMATTER_RIGHT_ALIGN },
              { key: "surveyTypeName", name: "Survey Type", dataSource: "surveyTypeName" },
              { key: "isAppointment", name: "Appt Required", dataSource: "isAppointment", dataType: DATA_TYPE_BOOLEAN },
              {
                key: "button",
                name: "",
                width: 100,
                dataSource: "button",
                filterType: FILTER_TYPE_NONE,
                tooltip: () => null
              },
              {
                key: "wasEdited",
                dataSource: "wasEdited",
                visible: false
              },
            ]}
            data={miles}
            emptyButtonText={"Loading Summary Data"}
            actions={actions}
          />}
      </MCard>
      {editDialogBox()}
    </>
  );
};

export default CaseMileage;